import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Trophy, Lock, Award, Loader2, Music2, Instagram, Youtube, Facebook, AtSign, Flame, Snowflake } from "lucide-react";

const PLAT_ICON = { tiktok: Music2, instagram: Instagram, youtube: Youtube, facebook: Facebook };

const fireConfetti = () => {
  const end = Date.now() + 900;
  const colors = ["#FE2C55", "#00F2FE", "#ffffff"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 65, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 65, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
};

const fmt = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n ?? 0);
};

const Medallion = ({ label, earned, i }) => (
  <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
    className="flex flex-col items-center gap-2" data-testid={`badge-${label}${earned ? "-earned" : "-locked"}`}>
    <div className={`relative w-16 h-16 rounded-full flex items-center justify-center border-2 ${
      earned ? "border-primary/60 bg-primary/10 shadow-[0_0_20px_rgba(0,242,254,0.35)]" : "border-white/10 bg-white/5"}`}>
      {earned ? <Award className="w-7 h-7 text-primary" /> : <Lock className="w-5 h-5 text-muted-foreground" />}
    </div>
    <span className={`text-xs font-bold ${earned ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
  </motion.div>
);

const Profile = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/badges").then((r) => setData(r.data)).catch(() => toast.error("Could not load badges")),
      api.get("/streak").then((r) => setStreak(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // Celebrate the first time a creator reaches a new streak reward milestone.
  useEffect(() => {
    if (!streak || !user?.email) return;
    const earned = (streak.rewards || []).filter((r) => r.earned);
    if (!earned.length) return;
    const top = earned[earned.length - 1];
    const key = `streak-reward-${user.email}`;
    const seen = Number(localStorage.getItem(key) || 0);
    if (top.weeks > seen) {
      localStorage.setItem(key, String(top.weeks));
      if (seen > 0 || streak.current_streak >= top.weeks) {
        fireConfetti();
        toast.success(`🔥 New streak reward unlocked: ${top.label} (${top.weeks} weeks)!`);
      }
    }
  }, [streak, user]);

  const tiers = data?.tiers || [];

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        {/* Profile header */}
        <div className="flex items-center gap-5 mb-8">
          <div className="w-20 h-20 rounded-2xl bg-primary/15 flex items-center justify-center text-primary font-heading text-3xl font-black shrink-0">
            {user?.picture ? <img src={user.picture} alt="" className="w-20 h-20 rounded-2xl object-cover" /> : (user?.name?.[0] || "C").toUpperCase()}
          </div>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">{user?.name || "Creator"}</h1>
            <p className="text-muted-foreground">{user?.email}</p>
            <div className="mt-2 flex items-center gap-2">
              {user?.plan && <Badge className="bg-primary/15 text-primary border border-primary/30 capitalize">{user.plan} plan</Badge>}
              {data && <Badge className="bg-white/5 border border-white/10"><Trophy className="w-3 h-3 mr-1 text-chart-4" />{data.total_earned} badges</Badge>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <Trophy className="w-5 h-5 text-chart-4" />
          <h2 className="font-heading text-xl font-bold">Trophy Case</h2>
        </div>

        {streak && (
          <div className="rounded-xl border border-border/50 bg-card p-6 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="streak-card">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${streak.current_streak > 0 ? "bg-chart-4/15 text-chart-4" : "bg-white/5 text-muted-foreground"}`}>
                <Flame className="w-7 h-7" fill={streak.current_streak > 0 ? "currentColor" : "none"} />
              </div>
              <div>
                <div className="font-heading text-3xl font-black" data-testid="streak-weeks">{streak.current_streak} <span className="text-lg">week{streak.current_streak === 1 ? "" : "s"}</span></div>
                <div className="text-sm text-muted-foreground">posting streak · {streak.this_week_posted ? "logged this week ✅" : "post this week to keep it alive"}</div>
                {streak.freeze && (
                  <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 border ${
                    streak.freeze.available ? "text-primary border-primary/30 bg-primary/10" : "text-foreground/80 border-white/15 bg-white/5"}`} data-testid="streak-freeze">
                    <Snowflake className={`w-3.5 h-3.5 ${streak.freeze.available ? "text-primary" : "text-sky-300"}`} />
                    <span>{streak.freeze.available ? "Streak Freeze ready" : "Streak Freeze used"}</span>
                    <span className="text-muted-foreground">· 1 free miss / {streak.freeze.month}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-6 text-center">
              <div><div className="font-heading text-xl font-black">{streak.longest_streak}</div><div className="text-xs text-muted-foreground">Best streak</div></div>
              <div><div className="font-heading text-xl font-black">{streak.total_posted}</div><div className="text-xs text-muted-foreground">Posts done</div></div>
              {streak.current_streak >= 4 && <Badge className="bg-chart-4/15 text-chart-4 border border-chart-4/30 self-center"><Flame className="w-3 h-3 mr-1" />{streak.current_streak >= 12 ? "On Fire" : "Consistent"}</Badge>}
            </div>
          </div>
        )}

        {streak?.rewards?.length > 0 && (
          <div className="rounded-xl border border-border/50 bg-card p-6 mb-6" data-testid="streak-rewards">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-chart-4" />
                <h3 className="font-heading font-semibold">Streak Rewards</h3>
              </div>
              {streak.next_reward && (
                <span className="text-xs text-muted-foreground" data-testid="streak-next-reward">
                  Next: <b className="text-foreground">{streak.next_reward.label}</b> · {streak.next_reward.remaining} week{streak.next_reward.remaining === 1 ? "" : "s"} to go
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
              {streak.rewards.map((r, i) => (
                <motion.div key={r.weeks} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                  className="flex flex-col items-center gap-2 text-center" data-testid={`streak-reward-${r.weeks}${r.earned ? "-earned" : "-locked"}`}>
                  <div className={`relative w-16 h-16 rounded-full flex items-center justify-center border-2 ${
                    r.earned ? "border-chart-4/60 bg-chart-4/10 shadow-[0_0_20px_rgba(254,44,85,0.35)]" : "border-white/10 bg-white/5"}`}>
                    {r.earned ? <Flame className="w-7 h-7 text-chart-4" fill="currentColor" /> : <Lock className="w-5 h-5 text-muted-foreground" />}
                  </div>
                  <span className={`text-xs font-bold leading-tight ${r.earned ? "text-foreground" : "text-muted-foreground"}`}>{r.label}</span>
                  <span className="text-[10px] text-muted-foreground">{r.weeks} wk</span>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : !data || data.platforms.length === 0 ? (
          <div className="rounded-2xl border border-border/50 bg-card p-12 text-center text-muted-foreground" data-testid="badges-empty">
            Add a platform and start growing to unlock milestone badges!
          </div>
        ) : (
          <div className="space-y-6">
            {data.platforms.map((p) => {
              const Icon = PLAT_ICON[p.platform] || Music2;
              return (
                <div key={p.platform} className="rounded-xl border border-border/50 bg-card p-6" data-testid={`badge-platform-${p.platform}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
                      <div>
                        <div className="font-heading font-semibold">{p.platform_name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><AtSign className="w-3 h-3" />{p.handle} · {fmt(p.current)} followers</div>
                      </div>
                    </div>
                    <Badge className="bg-primary/10 text-primary border border-primary/20">{p.earned.length}/{tiers.length} unlocked</Badge>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
                    {tiers.map((t, i) => (
                      <Medallion key={t.tier} label={t.label} earned={p.earned.includes(t.tier)} i={i} />
                    ))}
                  </div>
                  {p.next && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className="text-muted-foreground">Next: <b className="text-foreground">{p.next.label}</b> badge</span>
                        <span className="text-primary font-semibold">{fmt(p.next.remaining)} to go</span>
                      </div>
                      <Progress value={p.next.progress} className="h-2 bg-white/5" data-testid={`badge-next-progress-${p.platform}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardShell>
  );
};

export default Profile;
